'use client'

import React from 'react'
import Image from 'next/image'
import { cn } from '~shared/utils/cn'
import { LeadFormDialog } from '~widgets/lead-form/components/lead-form-dialog'
import { Button } from '~shared/components/button-v2'
// import { getHeroData } from 'modules'

import image1 from './images/image-1.jpg'
import image2 from './images/image-2.jpg'
import image3 from './images/image-3.jpg'
import image4 from './images/image-4.jpg'
import image5 from './images/image-5.jpg'
import ellipse1 from './images/ellipse1.svg'
import ellipse2 from './images/ellipse2.svg'
import ellipse3 from './images/ellipse3.svg'
import ellipse4 from './images/ellipse4.svg'
import ellipse5 from './images/ellipse5.svg'
import ellipse6 from './images/ellipse6.svg'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'

// ─── Types ────────────────────────────────────────────────────────────────────


// ─── Constants ────────────────────────────────────────────────────────────────

const DESCRIPTION =
  'Если нужно организовать обучение своих сотрудников – оставьте заявку. Мы подготовили для Вас индивидуальное предложение'
const BUTTON = 'Оставить заявку'

const images = [image1, image2, image3, image4, image5]

const ellipses = [ellipse1, ellipse2, ellipse3, ellipse4, ellipse5, ellipse6]

// ─── Component ────────────────────────────────────────────────────────────────

export const CorporateLeadSection = ({ program }: {
  program: Awaited<ReturnType<typeof getProgramData>>
}) => {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <section className='py-[56px] 2lg:py-[80px]'>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
        <div className='flex flex-col md:flex-row md:items-center gap-[40px] md:gap-[32px]'>
          {/* ─── Left: text + button ─── */}
          <div className='flex flex-col flex-1'>
            <h2 className='text-[32px] md:text-[40px] 2lg:text-[48px] font-semibold mb-[16px] 2lg:mb-[32px]'>
              Нужна программа для сотрудников компании?
            </h2>
            <p className='text-[20px] 2lg:text-[24px] leading-[140%] mb-[40px] md:mb-[40px] 2lg:mb-[56px]'>
              {DESCRIPTION}
            </p>
            <Button
              size='slg'
              variant='primary'
              onClick={() => setIsOpen(true)}
              className='w-full md:max-w-[280px] 2lg:max-w-[280px]'>
              {BUTTON}
            </Button>
            <LeadFormDialog
              open={isOpen}
              onOpenChange={setIsOpen}
              program={{
                productName: program?.name ?? '',
                productId: program?.productUid ?? null,
                productCategory: program?.category?.name ?? '',
                productFaculty: program?.faculty?.name ?? '',
                institute: program?.institute?.name ?? ''
              }}
            />
          </div>

          {/* ─── Right: images grid ─── */}
          <div className='relative flex-1'>
            {/* Ellipses background */}
            <div className='absolute inset-0 grid grid-cols-3 grid-rows-2 gap-[16px] 2lg:gap-[32px] rotate-x-180 md:rotate-x-0 pointer-events-none'>
              {ellipses.map((src, idx) => (
                <div key={idx} className='relative w-full aspect-square'>
                  <Image
                    src={src}
                    alt={`ellipse-${idx + 1}`}
                    fill
                    className='object-contain'
                  />
                </div>
              ))}
            </div>

            {/* Photos grid */}
            <div className='relative grid grid-cols-2 grid-rows-2 gap-[16px] 2lg:gap-[32px] w-[82%] ml-auto h-full'>
              {images.map((src, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'relative aspect-square',
                    [0, 1].includes(idx) &&
                      'w-[73px] md:w-[49px] 2lg:w-[104px]',
                    [2, 3].includes(idx) &&
                      'w-[123px] md:w-[83px] 2lg:w-[176px]',
                    idx === 4 && 'w-[96px] md:w-[64px] 2lg:w-[136px]',
                    [0, 4].includes(idx) && 'self-start',
                    idx === 2 && 'self-center',
                    ![0, 4].includes(idx) && idx !== 2 && 'self-end',
                    [1, 2, 3].includes(idx)
                      ? 'justify-self-end'
                      : 'justify-self-start'
                  )}>
                  <Image
                    src={src}
                    alt={`corporate-image-${idx + 1}`}
                    fill
                    quality={100}
                    className={cn(
                      'object-contain',
                      '2lg:rounded-[16px] rounded-[8px]'
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
